import { useHandleResponse } from '@/utils/useHandleResponse';
import { CalendarOutlined, EditOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { DatesSetArg, EventClickArg, EventContentArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Col, Input, Row, Segmented, Spin, Table, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../../context/AuthProvider';
import { updatePropsMyActiveMentenancePlannigs } from '../../../../../contextRedux/MentenancePlanning/MentenancePlanningModel';
import { setMyActiveMentenancePlannings } from '../../../../../contextRedux/MentenancePlanning/MentenancePlanningStore';
import MentenancePlanningService from '../../../../../services/mentenancePlanning/MentenancePlanningService';
import { RootState } from '../../../../../store';
import '../../MentenancePlanningCalendarTab/MentenancePlanningCalendarTab.css';
import MentenancePlanningModal from '../../MentenancePlanningModal/MentenancePlanningModal';

const MyActiveMentenancePlanning: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const handleResponse = useHandleResponse();

  const reduxMentenancePlanning = useSelector((state: RootState) => state?.models?.mentenancePlanning);
  const mentenancePlannings = useSelector(
    (state: RootState) => state?.store?.mentenancePlanning?.myActiveRequestMentenancePlanningData ?? [],
  );

  const searchTerm = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsMyActiveMentenancePlannigs?.searchTerm,
  );
  const skip = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsMyActiveMentenancePlannigs?.skip,
  );
  const take = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsMyActiveMentenancePlannigs?.take,
  );
  const refresh = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsMyActiveMentenancePlannigs?.refresh,
  );

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });
  const currentPage =
    Math.floor(
      reduxMentenancePlanning?.propsMyActiveMentenancePlannigs?.skip /
        reduxMentenancePlanning?.propsMyActiveMentenancePlannigs?.take,
    ) + 1;
  const [isSearchTermInitialized, setIsSearchTermInitialized] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'table' | 'calendar'>('table');
  const [calendarFocusDate, setCalendarFocusDate] = useState<Dayjs>(dayjs());
  const pageSizes = [10, 20, 30, 50];
  const { profile } = useAuth();

  const STATUS_CONFIG: Record<number, { label: string; color: string }> = {
    1: { label: 'Netransmis', color: '#8c8c8c' },
    2: { label: 'Transmis', color: '#1677ff' },
    3: { label: 'A ajuns', color: '#52c41a' },
    4: { label: 'Finalizata', color: '#13c2c2' },
    5: { label: 'Cerere reprogramare', color: '#faad14' },
  };
  const getStatusDisplay = (statusId?: number, fallbackName?: string) => {
    if (statusId && STATUS_CONFIG[statusId]) return STATUS_CONFIG[statusId];
    return { label: fallbackName || 'Status necunoscut', color: '#722ed1' };
  };
  const getClientName = (record: any) =>
    record?.metadata?.company?.name ?? record?.company?.name ?? 'Client necunoscut';
  const getWorkPlaceName = (record: any) =>
    record?.metadata?.workPlace?.name ?? record?.workPlace?.name ?? 'Punct necunoscut';

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;
    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'searchTerm', value: termInUrl }));
    dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'skip', value: calculatedSkip }));
    dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'take', value: pageSizeInUrl }));

    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsSearchTermInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isSearchTermInitialized) {
      const queryParams = queryString.parse(location.search);
      const currentPage = Math.floor(skip / take) + 1;
      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined,
        };
        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isSearchTermInitialized, navigate, location.pathname, location.search]);

  const {
    data: _dates,
    refetch,
    isFetching: isMentenancePlanningLoading,
  } = useQuery({
    queryKey: ['mentenancePlannings', searchTerm, skip, take, refresh],
    queryFn: () =>
      MentenancePlanningService.searchMyActiveMentenancePlanning({
        assignedTechnicianId: profile?.id,
        isMyActiveMentenancePlannings: true,
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isWorkPlaceIncluded: true,
        isAssignedTechnicianIncluded: true,
        isMentenancePlanningSystemTypeIncluded: true,
        isInterventionFileIncluded: true,
        termen: searchTerm || undefined,
        skip,
        take,
      }).then((data) => {
        dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setMyActiveMentenancePlannings(data?.response));
        dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'refresh', value: false }));
        return data?.response;
      }),
    enabled: !!profile?.id,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: calendarMonthData = [] } = useQuery({
    queryKey: ['mentenancePlanningsCalendar', profile?.id, viewMode, calendarFocusDate.format('YYYY-MM')],
    queryFn: () =>
      viewMode === 'calendar'
        ? MentenancePlanningService.searchMyActiveMentenancePlanning({
            assignedTechnicianId: profile?.id,
            isMyActiveMentenancePlannings: true,
            isStatusIncluded: true,
            isCompanyIncluded: true,
            isWorkPlaceIncluded: true,
            isAssignedTechnicianIncluded: true,
            isMentenancePlanningSystemTypeIncluded: true,
            isInterventionFileIncluded: true,
            month: calendarFocusDate.month() + 1,
            year: calendarFocusDate.year(),
            take: 500,
            skip: 0,
          }).then((res) => res?.response ?? [])
        : Promise.resolve([]),
    enabled: !!profile?.id && viewMode === 'calendar',
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2,
  });

  const calendarEvents = React.useMemo(() => {
    if (viewMode !== 'calendar') return [];
    return (calendarMonthData as any[])
      .map((record: any) => {
        const eventDate = record.appointment || record.dateEnd;
        if (!eventDate || !dayjs(eventDate).isValid()) return null;
        const statusDisplay = getStatusDisplay(record.statusId, record?.status?.name);
        return {
          id: record.id,
          title: `#${record.number || '-'} ${getClientName(record)}`,
          start: dayjs(eventDate).format('YYYY-MM-DD'),
          allDay: true,
          backgroundColor: statusDisplay.color,
          borderColor: statusDisplay.color,
          textColor: '#ffffff',
          extendedProps: { record, statusLabel: statusDisplay.label },
        };
      })
      .filter(Boolean);
  }, [viewMode, calendarMonthData]);

  const { mutate: saveMentenancePlanning, isPending: isSavingMentenancePlanning } = useMutation({
    mutationFn: (formData: any) => MentenancePlanningService.upsert(formData.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, `Mentenanta ${data?.response?.number} a fost modificata cu succes`);
        if (data?.response?.statusId == 5) {
          MentenancePlanningService.sendNotification(data?.response);
        }
        refetch();
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });

  useEffect(() => {
    if (isSearchTermInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isSearchTermInitialized, refetch]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'searchTerm', value }));
      dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'skip', value: 0 }));
    }, 500),
    [dispatch],
  );

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  const handleViewInfo = (record: any) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };

  const handleCalendarEventClick = (arg: EventClickArg) => {
    const record = arg.event.extendedProps?.record;
    if (record) handleViewInfo(record);
  };

  const handleDatesSet = (arg: DatesSetArg) => {
    const start = dayjs(arg.view.currentStart);
    setCalendarFocusDate(start);
  };

  const renderEventContent = (eventContent: EventContentArg) => {
    const record = eventContent.event.extendedProps?.record as any;
    if (!record) return null;
    const statusDisplay = getStatusDisplay(record.statusId, record?.status?.name);
    return (
      <div className='maintenance-calendar-event-content'>
        <div className='maintenance-calendar-event-header'>
          <span className='maintenance-calendar-event-number'>#{record.number ?? '-'}</span>
          <Tag color={statusDisplay.color} className='maintenance-calendar-event-status'>
            {statusDisplay.label}
          </Tag>
        </div>
        <div className='maintenance-calendar-event-text maintenance-calendar-event-client'>{getClientName(record)}</div>
        <div className='maintenance-calendar-event-text'>{getWorkPlaceName(record)}</div>
      </div>
    );
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  const handleViewRecord = (record: any, actionType: number, reschedulingReason?: any) => {
    if (actionType == 4) {
      const url = `/service-maintenance/interventionFile/new?mentenancePlanningId=${selectedRecord?.id}&companyId=${
        selectedRecord?.company?.id
      }&workPlaceId=${selectedRecord?.workPlace?.id}&systemTypes=${selectedRecord?.mentenancePlanningSystemTypes?.map(
        (s: any) => s?.systemType?.name,
      )}&assignedTechnicianId=${selectedRecord?.assignedTechnicianId}`;
      window.open(url, '_blank');
    } else {
      saveMentenancePlanning({
        ...record,
        statusId: actionType,
        history: [
          ...record.history,
          actionType == 5
            ? `[Cerere reprogramare] - ${dayjs().format('DD-MM-YYYY HH:mm')}] ${
                'Motivul reprogramarii: ' + !!reschedulingReason ? reschedulingReason : ''
              }`
            : actionType == 3
              ? `[A ajuns] - ${dayjs().format('DD-MM-YYYY HH:mm')}]`
              : actionType == 4
                ? `[Finalizata] - ${dayjs().format('DD-MM-YYYY HH:mm')}]`
                : '',
        ],
      });
    }

    setIsModalVisible(false); // Close the modal
  };

  const baseColumns: any = [
    { title: 'Numar', dataIndex: 'number', render: (number: any) => <Tag color={'blue'}>{number}</Tag> },
    {
      title: 'Beneficiar',
      key: 'company',
      render: (record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return <div>{company?.name}</div>;
      },
    },
    {
      title: 'Punct',
      dataIndex: '',
      render: (record: any) => {
        return <strong>{record?.metadata?.workPlace?.name ?? record?.workPlace?.name}</strong>;
      },
    },
    {
      title: 'Sistem',
      dataIndex: 'mentenancePlanningSystemTypes',
      key: 'mentenancePlanningSystemTypes',
      render: (mentenancePlanningSystemTypes: any[]) => (
        <>
          {mentenancePlanningSystemTypes && mentenancePlanningSystemTypes.length > 0 && (
            <span>
              {mentenancePlanningSystemTypes.length > 0
                ? mentenancePlanningSystemTypes?.map((type: any) => `[${type.systemType.name}]`).join(' ')
                : ''}
            </span>
          )}
        </>
      ),
    },
    {
      title: 'Perioada programata',
      dataIndex: '',
      render: (_value: any, record: any, _index: any) => {
        return (
          <>
            <span>{record.appointment ? moment(record.appointment).format('DD-MM-YYYY') : '-'}</span>
          </>
        );
      },
    },
    { title: 'Status', dataIndex: ['status', 'name'] },

    {
      title: 'Actiuni',
      dataIndex: 'actions',
      render: (_text: any, record: any) => {
        return <Button onClick={() => handleViewInfo(record)} icon={<EditOutlined />}></Button>;
      },
      align: 'center',
    },
  ];

  const paginationConfig = {
    current: currentPage,
    pageSize: take,
    total: reduxMentenancePlanning?.propsMyActiveMentenancePlannigs.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'skip', value: skipValue }));
      dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'skip', value: 0 }));
      dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };

  if (isMentenancePlanningLoading || isSavingMentenancePlanning) {
    return <Spin className='spinner' size='large'></Spin>;
  }

  return (
    <div className='table-page-no-h-scroll'>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align='middle'>
        <Col xs={24} sm={12}>
          <Segmented
            value={viewMode}
            onChange={(v) => setViewMode((v as 'table' | 'calendar') ?? 'table')}
            options={[
              { label: 'Tabel', value: 'table', icon: <UnorderedListOutlined /> },
              { label: 'Calendar', value: 'calendar', icon: <CalendarOutlined /> },
            ]}
            style={{ width: '100%', maxWidth: 320 }}
            block
          />
        </Col>
        {viewMode === 'table' && (
          <Col xs={24} sm={12}>
            <Input.Search
              placeholder='Search...'
              defaultValue={searchTerm}
              onChange={(e: any) => handleSearch(e.target.value)}
              style={{ width: '100%', maxWidth: 320 }}
              autoFocus={true}
            />
          </Col>
        )}
      </Row>

      {viewMode === 'table' && (
        <div className='table-scroll-wrapper'>
          <Table
            columns={baseColumns}
            dataSource={mentenancePlannings}
            pagination={paginationConfig}
            rowKey='number'
            scroll={{ x: 'max-content' }}
          />
        </div>
      )}

      {viewMode === 'calendar' && (
        <div className='maintenance-calendar-wrapper' style={{ minHeight: 360 }}>
          <FullCalendar
            plugins={[dayGridPlugin]}
            initialView='dayGridMonth'
            initialDate={calendarFocusDate.format('YYYY-MM-DD')}
            headerToolbar={{ left: 'prev,next today', center: 'title', right: '' }}
            locale='ro'
            firstDay={1}
            dayMaxEventRows={2}
            events={calendarEvents}
            eventClick={handleCalendarEventClick}
            eventContent={renderEventContent}
            datesSet={handleDatesSet}
            height='auto'
          />
        </div>
      )}

      <MentenancePlanningModal
        isModalVisible={isModalVisible}
        handleModalClose={handleModalClose}
        selectedRecord={selectedRecord}
        handleViewRecord={handleViewRecord}
        haveActions={true}
      />
    </div>
  );
};

export default MyActiveMentenancePlanning;
